/* The single cart module. Every add/change/update on the site goes through
   CartAPI; the drawer re-renders itself from the Section Rendering API.
   No other file may POST to /cart/*. */

import { bus, fetchSections, lazyDefine } from './critical.js';

const routes = window.__theme?.routes || {
  cartAdd: '/cart/add',
  cartChange: '/cart/change',
  cartUpdate: '/cart/update',
  cart: '/cart',
};

/* #277 (2026-09-09): gift-with-order memory, per browser session. 'dismissed' = the shopper removed the
   gift line themselves (never re-added this session); 'unpriced' = Shopify did not price the gift at $0
   (no discount set up), so it was removed and is not retried this session. */
export function giftFlag(kind, handle) {
  try { return sessionStorage.getItem(`gwp-${kind}:${handle}`) === '1'; } catch { return false; }
}
export function setGiftFlag(kind, handle) {
  try { sessionStorage.setItem(`gwp-${kind}:${handle}`, '1'); } catch { /* storage blocked: the per-page attempt cap still holds */ }
}

/* The rendered drawer carries its section-group instance id in section-id;
   requesting that id (not the filename) makes the Section Rendering API
   return the instance with its configured settings. */
function drawerSection() {
  return document.querySelector('cart-drawer')?.getAttribute('section-id') || 'cart-drawer';
}

/* #543 Fix 9: the script every Cloudflare challenge page loads (see post()). */
const CHALLENGE_PAGE = /\/cdn-cgi\/challenge-platform\/|_cf_chl_opt/;

async function post(url, body) {
  const response = await fetch(`${url}.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: drawerSection() }),
  });
  /* Shopify serves /cart/*.js as text/javascript with a JSON body, and
     errors (429/5xx pages) can be HTML — so parse defensively; never gate
     on Content-Type and never surface a raw SyntaxError. */
  let data = null;
  let text = '';
  try {
    text = await response.text();
    data = JSON.parse(text);
  } catch {
    /* non-JSON body (HTML error page) */
  }
  if (!response.ok || data === null) {
    const message = data?.description || data?.message || `Cart request failed (${response.status})`;
    const error = new Error(message);
    /* #368 (2026-09-16): the SERVER's own sentence, separately, for a surface that shows shoppers a
       failure and must not print "Cart request failed (429)" at them (the gift chooser's retry line).
       .message is unchanged for every existing caller. */
    error.cartDescription = data?.description || data?.message || null;
    error.cartStatus = response.status;
    /* #543 Fix 9 (2026-09-23): a real bot challenge, told apart from a plain refusal. Only a challenge is
       resolved by the page itself, so only a challenge may send the shopper to a full-page post (the gift
       chooser's #145 fallback); a 429, 422, 5xx or a lost connection keeps them on the page. Cloudflare marks
       its challenge answers with cf-mitigated: challenge; an older 403/503 challenge page carries its
       challenge-platform script. */
    error.cartChallenge =
      response.headers.get('cf-mitigated') === 'challenge' ||
      ((response.status === 403 || response.status === 503) && data === null && CHALLENGE_PAGE.test(text));
    throw error;
  }
  return data;
}

/* Mutations are numbered so render paths can reject a payload that resolved
   after (but was issued before) a newer one. */
let mutationCount = 0;

export const CartAPI = {
  async add(items, options = {}) {
    const mutation = ++mutationCount;
    const payload = Array.isArray(items) ? { items } : { items: [items] };
    const data = await post(routes.cartAdd, payload);
    bus.emit('cart:updated', { data, action: 'add', mutation });
    /* #429 (2026-09-16): options.opener travels with the open so the drawer can return focus to the
       control the shopper actually pressed. Without it the drawer falls back to document.activeElement,
       which for an add made from the gift chooser is the already-closing dialog — focus then landed on
       <body> at the top of a 17,000 px page. */
    if (options.openDrawer !== false && window.__theme?.cartDrawer) bus.emit('drawer:open', { drawer: 'cart', opener: options.opener || null });
    return data;
  },

  async change(lineKeyOrIndex, quantity, properties) {
    const mutation = ++mutationCount;
    const body = { id: lineKeyOrIndex, quantity };
    if (properties) body.properties = properties;
    const data = await post(routes.cartChange, body);
    bus.emit('cart:updated', { data, action: 'change', mutation });
    return data;
  },

  async update(updates) {
    const mutation = ++mutationCount;
    const data = await post(routes.cartUpdate, { updates });
    bus.emit('cart:updated', { data, action: 'update', mutation });
    return data;
  },
};

/* <cart-drawer section-id="cart-drawer"> wraps the server-rendered drawer.
   Markup contract:
   - [data-drawer-panel]   sliding panel
   - [data-drawer-inner]   region replaced on cart:updated
   - [data-drawer-close]   close triggers
   - [data-drawer-live]    persistent live region (mirrors the title after render)
   - [data-cart-error]     alert region inside the inner region: mutation errors and the
                           #267 "cart updated, refresh to see it" notice land here
   - [data-cart-checkout]  the checkout link: pressed state on tap (#543 Fix 8)
   Any element with [data-cart-remove] or [data-cart-qty] inside the drawer
   is wired automatically after each render. */
class CartDrawer extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => {
      if (event.target === this || event.target.closest('[data-drawer-close]')) this.close();
      /* #543 Fix 8 (2026-09-23, audit checkout-entry-4): checkout takes 1-2.7 s to paint on a phone and v2's
         button showed nothing meanwhile (control's goes busy). The theme's existing busy look
         (.btn[aria-busy='true']: dimmed, not pressable, so no second tap either), no new text. A click that
         opens a new tab leaves this page as it is. Cleared on the way back (pageshow below) and, should the
         navigation never happen, after 10 s. */
      const checkout = event.target.closest('[data-cart-checkout]');
      if (checkout && !event.defaultPrevented && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
        checkout.setAttribute('aria-busy', 'true');
        setTimeout(() => checkout.removeAttribute('aria-busy'), 10000);
      }
    });
    /* #244 (2026-09-07): the document-level handlers are kept so disconnectedCallback can unbind
       them — a theme-editor section reload replaces this element, and the old one must not keep
       rendering into a detached drawer. bus.on returns its own unsubscribe. */
    this.onKeydown = (event) => {
      if (!this.hasAttribute('open')) return;
      if (event.key === 'Escape') this.close();
      else if (event.key === 'Tab') this.trap(event);
    };
    document.addEventListener('keydown', this.onKeydown);
    this.unsubscribe = [
      bus.on('drawer:open', (event) => {
        if (event.detail?.drawer === 'cart') this.open(event.detail.opener);
      }),
      bus.on('cart:updated', (event) => this.render(event.detail?.data, event.detail?.mutation)),
    ];
    this.renderedMutation = 0;
    this.wire();
    this.autoGift();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    for (const off of this.unsubscribe || []) off();
    this.unsubscribe = [];
  }

  /* Keep Tab/Shift+Tab cycling inside the drawer while it is open. */
  trap(event) {
    const items = [...this.querySelectorAll('a[href], button:not([disabled]), input')];
    if (!items.length) return;
    const active = document.activeElement;
    const index = items.indexOf(active);
    const wrap = event.shiftKey
      ? index <= 0
      : index === items.length - 1 || (index < 0 && !this.contains(active));
    if (wrap) {
      event.preventDefault();
      items[event.shiftKey ? items.length - 1 : 0].focus();
    }
  }

  /* The page regions behind the drawer (layout/theme.liquid) sit inert while open. */
  setBackgroundInert(value) {
    for (const region of document.querySelectorAll('body > header, #main')) region.inert = value;
  }

  /* #429 (2026-09-16): `opener` is the control the caller knows was pressed. document.activeElement is
     only the fallback — it is right for the header cart button and a plain quick-add, and wrong for
     anything that hands the drawer over from a closing overlay (the gift chooser). Callable directly on
     the element, which is how assets/bogo-modal.js opens the drawer after a pick. */
  open(opener) {
    if (!this.hasAttribute('open')) this.opener = opener && opener.isConnected ? opener : document.activeElement;
    this.setAttribute('open', '');
    document.documentElement.classList.add('drawer-open');
    this.setBackgroundInert(true);
    this.querySelector('[data-drawer-panel]')?.focus();
  }

  close() {
    this.removeAttribute('open');
    document.documentElement.classList.remove('drawer-open');
    this.setBackgroundInert(false);
    if (this.opener?.isConnected) this.opener.focus();
    this.opener = null;
  }

  /* Parse a Section Rendering API payload down to its [data-drawer-inner] region.
     null when the section is missing/null or rendered without the region — both are
     Shopify-side render failures, not cart failures. */
  parseInner(html) {
    if (!html || typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector('[data-drawer-inner]');
  }

  /* Async since #267; callers ignore the promise. Never throws/rejects — a rendering
     failure must never read as a cart failure to mutating() or the bus. The success
     path has no await, so it still completes synchronously inside bus.emit exactly as
     before (mutating()'s aria-busy still covers the swap). */
  async render(data, mutation) {
    try {
      /* Stale payload (an older mutation resolving late) — never apply it. */
      if (mutation && mutation < this.renderedMutation) return;
      const id = drawerSection();
      let incoming = this.parseInner(data?.sections?.[id]);
      if (!incoming) {
        /* #267 (2026-09-09, Codex F06): the mutation itself succeeded — CartAPI already
           emitted cart:updated and opened the drawer — only the bundled section render
           came back null/empty, which used to leave the OPEN drawer showing the old
           cart. ONE retry through the Section Rendering API: a GET, never a re-POST. */
        let sections = null;
        try {
          sections = await fetchSections(id);
        } catch {
          /* retry failed — fall through to the notice */
        }
        /* Re-check after the await: a newer mutation may have rendered meanwhile, and a
           retry for an older one must never overwrite it (renderedMutation only moves
           forward). A drawer replaced by a theme-editor reload renders nothing. */
        if (!this.isConnected || (mutation && mutation < this.renderedMutation)) return;
        incoming = this.parseInner(sections?.[id]);
        if (!incoming) {
          this.noticeStale();
          return;
        }
      }
      this.swap(incoming, mutation);
    } catch {
      /* see above: render() never throws */
    }
  }

  /* Replace the inner region with a parsed incoming one, re-wire, restore focus. */
  swap(incoming, mutation) {
    const current = this.querySelector('[data-drawer-inner]');
    if (!current) return;
    const focused = document.activeElement;
    const hadFocus = this.contains(focused);
    current.replaceWith(incoming);
    if (mutation) this.renderedMutation = mutation;
    this.wire();
    /* #94: the swapped-in markup can contain [data-lazy-module] hosts the
       DOMContentLoaded lazyDefine pass never saw (the paired-state upsell
       scroll-slider) — re-arm observation/trigger listeners for them. */
    lazyDefine(this);
    this.mirrorTitle();
    this.autoGift();
    /* The swap dropped keyboard focus to <body>: land it back on the same
       qty control (matched by line key + pill side) or, failing that,
       the panel. */
    if (hadFocus && this.hasAttribute('open')) {
      let target = null;
      const { cartQty, step } = focused.dataset || {};
      if (cartQty) target = this.querySelector(`[data-cart-qty="${cartQty}"]${step ? `[data-step="${step}"]` : ''}`);
      (target || this.querySelector('[data-drawer-panel]'))?.focus();
    }
  }

  /* [data-drawer-live] sits outside the swapped region, so it survives renders and
     re-announces the (possibly unchanged) title. */
  mirrorTitle() {
    const title = this.querySelector('#cart-drawer-title');
    const live = this.querySelector('[data-drawer-live]');
    if (live && title) live.textContent = title.textContent.trim();
  }

  /* #267 (2026-09-09, Codex F06): both renders failed — the cart changed but the drawer
     could not be refreshed. Say so in the drawer's alert region with a link to the cart
     page. Strings come from window.__theme.strings (layout/theme.liquid, read from the
     locale file); English is only the fallback. DOM APIs, never innerHTML. The next
     successful render swaps the region out, clearing the notice. */
  noticeStale() {
    const error = this.querySelector('[data-cart-error]');
    if (error) {
      const strings = window.__theme?.strings || {};
      const link = document.createElement('a');
      link.href = routes.cart;
      link.className = 'underline';
      link.textContent = strings.viewCart || 'View cart';
      error.replaceChildren(`${strings.cartUpdated || 'Your cart was updated. Refresh the page to see it.'} `, link);
    }
    this.mirrorTitle();
  }

  wire() {
    for (const button of this.querySelectorAll('[data-cart-remove]')) {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        if (button.dataset.giftLine) setGiftFlag('dismissed', button.dataset.giftLine); /* #277: the shopper said no */
        this.mutating(() => CartAPI.change(button.dataset.cartRemove, 0));
      });
    }
    /* #90 pill: -/+ buttons carry the ABSOLUTE target qty in data-qty
       (server-computed), so repeated clicks are idempotent. */
    for (const button of this.querySelectorAll('button[data-cart-qty][data-qty]')) {
      button.addEventListener('click', () => {
        this.mutating(() => CartAPI.change(button.dataset.cartQty, parseInt(button.dataset.qty, 10)));
      });
    }
    for (const input of this.querySelectorAll('input[data-cart-qty]')) {
      input.addEventListener('change', () => {
        const quantity = Math.max(0, parseInt(input.value, 10) || 0);
        this.mutating(() => CartAPI.change(input.dataset.cartQty, quantity));
      });
    }
  }

  /* #277 (2026-09-09): gift with order. The drawer's Liquid (snippets/active-gift.liquid) decides what the
     cart SHOULD hold and prints it on [data-gift-rules] inside the swapped region: data-add = the gift
     variant to add (order qualifies, gift missing), data-remove = the gift line to take out (order no
     longer qualifies, or data-unpriced: Shopify did not price it at $0 — a gift the store would charge for
     never stays). This runs once per render and only executes. Guards: a dismissed or unpriced gift is
     not added again this session; one request in flight at a time; at most four attempts per page load
     so no render loop can run away; never opens the drawer; failures are silent (the next render decides
     again). The gift's line property _gwp = the entry's handle, hidden from checkout and excluded from
     every BOGO count. */
  autoGift() {
    const rules = this.querySelector('[data-gift-rules]');
    if (!rules || this.giftBusy) return;
    const handle = rules.dataset.handle || '';
    if (!handle) return; /* no entry handle, no action — the rules are only trusted with one */
    this.giftAttempts = this.giftAttempts || 0;
    let action = null;
    if (rules.dataset.remove) {
      if (rules.dataset.unpriced) setGiftFlag('unpriced', handle);
      action = () => CartAPI.change(rules.dataset.remove, 0);
    } else if (rules.dataset.add && !giftFlag('dismissed', handle) && !giftFlag('unpriced', handle)) {
      action = () => CartAPI.add({ id: parseInt(rules.dataset.add, 10), quantity: 1, properties: { _gwp: handle } }, { openDrawer: false });
    }
    if (!action || this.giftAttempts >= 4) return;
    this.giftAttempts += 1;
    this.giftBusy = true;
    /* The request's own cart:updated re-renders the drawer BEFORE this promise settles, and that render's
       autoGift() call returns on giftBusy — so decide once more after settling (verified 09-09: without
       this the unpriced-gift removal never ran). The attempt cap bounds the chain. */
    action().catch(() => {}).finally(() => { this.giftBusy = false; this.autoGift(); });
  }

  async mutating(fn) {
    this.setAttribute('aria-busy', 'true');
    try {
      await fn();
    } catch (error) {
      this.querySelector('[data-cart-error]')?.replaceChildren(error.message);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }
}

/* <product-form> progressive enhancement: any <form action="/cart/add">
   inside gets AJAX submit through CartAPI, including quick-add cards,
   sticky ATC, and upsells — one code path.
   Contract:
   - [data-form-error]  error region inside the element. Markup may ship one; when it
     doesn't, connect creates one directly after the submit button (#266, 2026-09-09,
     Codex F08). Created up front, not in the catch: a live region has to exist before
     text lands in it to be announced.
   - `product-form:added` — bubbling CustomEvent dispatched on the <form> after a
     successful add, detail { item, response }: item = the payload sent to
     /cart/add.js, response = the JSON it returned (#268, 2026-09-09, Codex F16;
     quiz.js reports from this instead of from submit). */
class ProductFormElement extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector('form[action*="/cart/add"]');
    if (!form) return;
    this.ensureErrorRegion(form);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      /* Single-flight: set synchronously before any await so a double
         submit (rapid clicks, sticky + main) can't add twice. */
      if (form.dataset.busy) return;
      form.dataset.busy = 'true';
      const submit = form.querySelector('[type="submit"]');
      /* aria-busy only (CSS blocks pointer, dataset.busy blocks resubmit):
         disabling would drop focus to <body> and break the drawer's
         focus-restore-to-opener. */
      submit?.setAttribute('aria-busy', 'true');
      try {
        const formData = new FormData(form);
        const item = {
          id: parseInt(formData.get('id'), 10),
          quantity: parseInt(formData.get('quantity'), 10) || 1,
        };
        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = parseInt(sellingPlan, 10);
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && value) properties[match[1]] = value;
        }
        if (Object.keys(properties).length) item.properties = properties;
        const response = await CartAPI.add(item);
        this.querySelector('[data-form-error]')?.replaceChildren();
        /* #268 (2026-09-09, Codex F16): report AFTER the add succeeded. Listener errors
           stay inside dispatchEvent and cannot route this submit into the catch. */
        form.dispatchEvent(new CustomEvent('product-form:added', { bubbles: true, detail: { item, response } }));
      } catch (error) {
        /* #543 Fix 9 review (code-review-5, 2026-09-23): the store's own sentence when Shopify gave one, else the
           theme's cart.add_failed line (layout/theme.liquid) — never "Cart request failed (429)" or the browser's
           "Failed to fetch". The gift chooser already answers in that order (bogo-modal.js errorText()). */
        const message = error.cartDescription || window.__theme?.strings?.addFailed || error.message;
        this.querySelector('[data-form-error]')?.replaceChildren(message);
      } finally {
        delete form.dataset.busy;
        submit?.removeAttribute('aria-busy');
      }
    });
  }

  /* #266 (2026-09-09, Codex F08): quick-add forms that shipped without a
     [data-form-error] region swallowed cart errors (the catch wrote into nothing).
     Same classes as the hand-written regions (kit-form, main-product) — all present in
     the built main.css. Idempotent: a second connect finds the first region. */
  ensureErrorRegion(form) {
    if (this.querySelector('[data-form-error]')) return;
    const region = document.createElement('div');
    region.setAttribute('data-form-error', '');
    region.className = 'text-c-accent-error mt-2 text-sm empty:hidden';
    region.setAttribute('aria-live', 'polite');
    const submit = form.querySelector('[type="submit"]');
    if (submit) submit.after(region);
    else form.append(region);
  }
}

/* Guarded for browsers without importmap support (theme.liquid maps the
   unversioned module URL onto this one): a second evaluation must not
   throw and kill the importing module (cart-page.js / customer.js). */
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);
if (!customElements.get('product-form')) customElements.define('product-form', ProductFormElement);

/* Header cart buttons: [data-drawer-trigger="cart"] opens the drawer instead
   of navigating; [data-cart-count] badges track the live count (cart/add.js
   doesn't return item_count, so fall back to /cart.js). */
function wireDrawerTriggers(root = document) {
  for (const trigger of root.querySelectorAll('[data-drawer-trigger="cart"]')) {
    if (trigger.dataset.drawerWired) continue;
    trigger.dataset.drawerWired = 'true';
    trigger.addEventListener('click', (event) => {
      event.preventDefault();
      bus.emit('drawer:open', { drawer: 'cart' });
    });
  }
}
document.addEventListener('DOMContentLoaded', () => wireDrawerTriggers());
document.addEventListener('shopify:section:load', (event) => wireDrawerTriggers(event.target));

let badgeMutation = 0;
bus.on('cart:updated', async (event) => {
  const mutation = event.detail?.mutation || 0;
  let cart = event.detail?.data;
  if (typeof cart?.item_count !== 'number') {
    try {
      cart = await (await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } })).json();
    } catch {
      return; /* keep the current badge on a failed refresh */
    }
  }
  if (mutation && mutation < badgeMutation) return; /* stale count from an older mutation */
  if (mutation) badgeMutation = mutation;
  for (const el of document.querySelectorAll('[data-cart-count]')) {
    el.textContent = cart.item_count;
    el.classList.toggle('hidden', cart.item_count === 0);
    el.classList.toggle('flex', cart.item_count > 0);
    el.closest('[data-drawer-trigger]')?.classList.toggle('empty-cart', cart.item_count === 0);
  }
});

/* #543 Fix 1 (2026-09-23, audit checkout-entry-1): the drawer and the badges are printed into the page when it is
   served and only a cart:updated redraws them. A page the browser shows AGAIN from history carries the cart as it
   was when that HTML was first served — restored from the back/forward cache (pageshow persisted), or rebuilt from
   the browser's own cache on Back (navigation type back_forward, persisted false: what iOS does when it dropped the
   kept page while the shopper was on checkout). The shopper then met "Your cart is empty", no count and no checkout
   button while the cart held the order (v2 2 of 2 runs, control 0 of 2 — control re-reads the cart on every load).
   Such a page re-reads the cart once: /cart.js and the drawer section, both GETs, never a cart write, handed to the
   existing cart:updated renderers under a fresh mutation number, so a real mutation started meanwhile still wins.
   A normal page load spends nothing. */
async function refreshFromHistory() {
  const mutation = ++mutationCount;
  try {
    /* a failed section read is not fatal: render() retries it once itself, then prints the #267 notice */
    const [cart, sections] = await Promise.all([
      fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((response) => response.json()),
      fetchSections(drawerSection()).catch(() => null),
    ]);
    if (typeof cart?.item_count !== 'number') return;
    bus.emit('cart:updated', { data: { ...cart, sections }, action: 'refresh', mutation });
  } catch {
    /* read failed: the page keeps what it was served with, exactly as before */
  }
}
window.addEventListener('pageshow', (event) => {
  /* #543 Fix 8: a checkout link pressed before the page was kept comes back pressable */
  if (event.persisted) for (const el of document.querySelectorAll('[data-cart-checkout][aria-busy]')) el.removeAttribute('aria-busy');
  const navigation = performance.getEntriesByType?.('navigation')?.[0];
  if (event.persisted || navigation?.type === 'back_forward') refreshFromHistory();
});
